'use client'

import { useEffect, useRef, useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { useCustomerChatSocket } from '@/lib/customerChatSocketProvider'
import { useCart } from './useCart'
import { navigateUrl } from '@/utils/client-request'

type UseCustomerChatArgs = {
  vendor: any
  defaultBotName?: string
  defaultBotAvatar?: string
}

export function useCustomerChat({ vendor, defaultBotName = 'Дэлгүүрийн туслах', defaultBotAvatar }: UseCustomerChatArgs) {
  const pathname = usePathname()
  const router = useRouter()
  const { loading, addToCart, fetchCart } = useCart()
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const [isMounted, setIsMounted] = useState(false)
  const [inputMessage, setInputMessage] = useState('')
  const [textareaRows, setTextareaRows] = useState(1)
  const [selectedVariants, setSelectedVariants] = useState<Record<string, string>>({})
  const [initialConfig, setInitialConfig] = useState<any>(null)

  const {
    socket, isConnected, currentSession, config, messages, isLoading, error,
    startChat, sendMessage, setTyping, isAiTyping,
    isChatExpanded, setIsChatExpanded,
    isChatOpen, setIsChatOpen,
  } = useCustomerChatSocket()

  useEffect(() => { setIsMounted(true) }, [])

  useEffect(() => {
    if (!socket || !isConnected || !vendor?.id || initialConfig || config) return
    socket.emit('customer-get-config', { vendorId: vendor.id }, (res: any) => {
      if (res?.status === 200 && res.config) setInitialConfig(res.config)
    })
  }, [socket, isConnected, vendor?.id])

  useEffect(() => {
    if (!socket) return
    const handleCartUpdated = () => fetchCart()
    socket.on('customer-cart-updated', handleCartUpdated)
    return () => { socket.off('customer-cart-updated', handleCartUpdated) }
  }, [socket, fetchCart])

  useEffect(() => {
    if (isChatOpen && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, isChatOpen, isAiTyping])

  useEffect(() => {
    setIsChatOpen(false)
    setIsChatExpanded(false)
  }, [vendor?.id])

  useEffect(() => {
    if (isChatOpen && !currentSession && isConnected) {
      handleStartChat()
    }
  }, [isChatOpen, isConnected])

  const activeConfig = config || initialConfig
  const displayBotName = activeConfig?.botName || defaultBotName
  const displayBotAvatar = activeConfig?.appearance?.avatar || defaultBotAvatar
  const displayPrimaryColor = activeConfig?.appearance?.color || vendor?.color?.primary
  const chatPosition: 'bottom-left' | 'bottom-right' = activeConfig?.appearance?.position || 'bottom-right'
  const configMethods: string[] = activeConfig?.paymentMethods || ['transfer']

  const handleStartChat = async () => {
    try { await startChat(vendor?.id) }
    catch (err) { console.error('Failed to start chat:', err) }
  }

  const handleSendMessage = async (e?: React.FormEvent) => {
    if (e) e.preventDefault()
    if (!inputMessage.trim() || !currentSession || inputMessage.length > 200) return
    const messageText = inputMessage.trim()
    setInputMessage('')
    setTextareaRows(1)
    setTyping(false)
    try { await sendMessage(messageText) }
    catch (err) { console.error('Failed to send message:', err) }
  }

  const handleClose = () => setIsChatOpen(false)

  const handleInputChange = (value: string) => {
    setInputMessage(value)
    setTyping(value.length > 0)
    if (!value.trim()) {
      setTextareaRows(1)
    } else {
      setTextareaRows(Math.min(value.split('\n').length, 3))
    }
  }

  const handleSuggestedQuestion = async (question: string) => {
    if (!currentSession) return
    try { await sendMessage(question) }
    catch (err) { console.error('Failed to send message:', err) }
  }

  const handleAddToCart = async (product: any, variantId?: string) => {
    const firstAvailableVariant = product.variants && product.variants.length > 0 ? product.variants[0] : null
    const selectedVariantId = variantId || firstAvailableVariant?.id
    if (!selectedVariantId) return
    const result = await addToCart({ productId: product.id, quantity: 1, variantId: selectedVariantId })
    if (result?.requiresLogin) {
      setIsChatOpen(false)
      router.push(navigateUrl('/checkout/login', pathname))
    }
  }

  const handleVariantChange = (productId: string, variantId: string) => {
    setSelectedVariants(prev => ({ ...prev, [productId]: variantId }))
  }

  const goToOrder = (customId: string, phone: string) => {
    setIsChatOpen(false)
    router.push(navigateUrl(`/order/${customId}/${phone}`, pathname))
  }

  const goToProduct = (customId: string) => {
    setIsChatOpen(false)
    router.push(navigateUrl(`/product/${customId}`, pathname))
  }

  return {
    isMounted, messagesEndRef,
    inputMessage, textareaRows, selectedVariants,
    loading,
    socket, isConnected, currentSession, messages, isLoading, error,
    isAiTyping, isChatExpanded, setIsChatExpanded, isChatOpen, setIsChatOpen,
    activeConfig, displayBotName, displayBotAvatar, displayPrimaryColor, chatPosition, configMethods,
    handleStartChat, handleSendMessage, handleClose, handleInputChange,
    handleSuggestedQuestion, handleAddToCart, handleVariantChange,
    goToOrder, goToProduct,
  }
}
